import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Avatar, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { showToast } from "../../store/toast/toastSlice";
import BaseButton from "../../components/ui/BaseButton";
import BaseTextField from "../../components/ui/BaseTextField";
import { getFreelanceCompanyMembers, assignMemberToWorkOrder } from "../../services/api/freelanceCompany";
import { useUpdateWorkOrder } from "../../hooks/useUpdateWorkOrder";
import { debounce } from "../../utils/helpers";

const AssignToMemberModal = ({ open, onClose, refetchUserAllowedAction }) => {
  const dispatch = useDispatch();
  const workOrderDetail = useSelector((state) => state.workOrder.workOrderDetails);
  const { updateWorkOrder } = useUpdateWorkOrder();
  const [assigningMemberId, setAssigningMemberId] = useState(null);
  const [search, setSearch] = useState("");
  const [queryParams, setQueryParams] = useState({ page: 1, limit: 50, search: "" });

  const { isLoading, data: membersData } = useQuery({
    queryKey: ["companyMembers", queryParams.page, queryParams.limit, queryParams.search],
    queryFn: () => getFreelanceCompanyMembers({ ...queryParams, approvedOnly: true }),
    enabled: open,
  });

  const { mutateAsync: assignMember } = useMutation({
    mutationFn: assignMemberToWorkOrder,
  });

  const debouncedSearchRef = useRef(
    debounce((val) => {
      setQueryParams((prev) => ({ ...prev, search: val, page: 1 }));
    }, 500),
  );

  async function handleAssign(memberId) {
    try {
      setAssigningMemberId(memberId);
      await assignMember({ workOrderId: workOrderDetail.id, memberId });
      dispatch(showToast({ message: "Member assigned successfully", type: "success" }));
      await updateWorkOrder();
      await refetchUserAllowedAction();
      onClose();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to assign member",
          type: "error",
        }),
      );
    } finally {
      setAssigningMemberId(null);
    }
  }

  const members = membersData?.data?.data || [];
  const totalCount = membersData?.data?.totalCount || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="flex items-center justify-between">
        <span className="text-base font-semibold">Assign to Team Member</span>
        <IconButton onClick={onClose} size="small">
          <i className="fa-solid fa-xmark text-sm" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <div className="mb-3">
          <BaseTextField
            placeholder="Search members"
            onChange={(e) => {
              const val = e.target.value;
              setSearch(val);
              debouncedSearchRef.current(val);
            }}
            value={search}
            startAdornment={<i className="fa-solid fa-magnifying-glass text-sm" />}
            className="w-full"
          />
        </div>

        {isLoading ? (
          <div className="py-6 text-center text-sm text-[#A5A5A5]">Loading members...</div>
        ) : members.length === 0 ? (
          <div className="py-6 text-center text-sm text-[#A5A5A5]">No team members found</div>
        ) : (
          <div className="flex flex-col gap-2">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between px-3 py-2 rounded-lg border border-zinc-100 hover:bg-zinc-50"
              >
                <div className="flex items-center gap-3">
                  <Avatar sx={{ width: 36, height: 36 }} />
                  <div>
                    <p className="text-sm font-medium capitalize text-dark">
                      {member.user?.firstName} {member.user?.lastName}
                    </p>
                    <p className="text-xs text-[#A5A5A5]">{member.user?.email}</p>
                  </div>
                </div>
                <BaseButton
                  isLoading={assigningMemberId === member.id}
                  disabled={!!assigningMemberId}
                  onClick={() => handleAssign(member.id)}
                  style={{ paddingTop: 2, paddingBottom: 2 }}
                  className="bg-primary text-white text-xs min-w-[80px] h-8"
                  fullWidth={false}
                >
                  Assign
                </BaseButton>
              </div>
            ))}
          </div>
        )}

        {totalCount > members.length && (
          <p className="text-xs text-[#A5A5A5] mt-3 text-center">
            Showing {members.length} of {totalCount} members
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssignToMemberModal;
